import type { ComponentProps, ReactNode } from "react";

import { Label } from "../label";
import { Tabs, TabsList, TabsTrigger } from "../tabs";
import { getFieldError } from "./get-field-error";
import { useFieldContext } from "./form";

interface FormTabsOption {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface FormTabsProps extends Omit<
  ComponentProps<typeof Tabs>,
  "value" | "onValueChange" | "defaultValue" | "children"
> {
  label?: string;
  labelClassName?: string;
  options: FormTabsOption[];
  listClassName?: string;
  triggerClassName?: string;
  showErrorMessage?: boolean;
}

function FormTabs({
  label,
  labelClassName,
  options,
  listClassName,
  triggerClassName,
  showErrorMessage = true,
  ...props
}: FormTabsProps) {
  const field = useFieldContext<string>();

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <Label htmlFor={field.name} className={labelClassName}>
          {label}
        </Label>
      ) : null}
      <Tabs
        value={field.state.value ?? ""}
        onValueChange={(value: string) => {
          if (value) {
            field.handleChange(value);
          }
        }}
        {...props}
      >
        <TabsList className={listClassName}>
          {options.map((option) => (
            <TabsTrigger
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={triggerClassName}
            >
              {option.icon}
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {showErrorMessage && field.state.meta.errors.length > 0 ? (
        <span className="text-sm text-destructive">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      ) : null}
    </div>
  );
}

export { FormTabs };
export type { FormTabsOption };
